import { Button, DefinitionList, notify, Section, StatusBadge, TextField, Toggle } from "@flexfreight/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, ApiError } from "../../api/client";
import { IntegrationBody } from "./integrationParts";

/* Admin Center > Integrations > Google Maps (address autocomplete). The key is write-only: the API returns only whether
 * one is saved. Address suggestions in the portal and console go through our own /v1, never straight to Google. */

type LastTest = { at: string; ok: boolean | null; message: string | null } | null;
export type GoogleMapsView = {
  keySaved: boolean;
  configured: boolean;
  enabled: boolean;
  lastTest: LastTest;
  status: "Awaiting credentials" | "Off" | "Connected";
};

const when = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
const KEY = ["console", "integrations", "google-maps"];
const URL = "/v1/console/integrations/google-maps";

export function GoogleMapsCard({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => api<GoogleMapsView>(URL) });
  const v = q.data;
  const [key, setKey] = useState("");
  const set = (r: GoogleMapsView) => qc.setQueryData(KEY, r);
  const save = useMutation({
    mutationFn: (enabled: boolean) => api<GoogleMapsView>(URL, { method: "PUT", body: { ...(key.trim() ? { apiKey: key.trim() } : {}), enabled } }),
    onSuccess: (r) => {
      set(r);
      setKey("");
      notify.success(r.enabled ? "Address suggestions are on" : "Google Maps settings saved");
    },
    onError: (e) => notify.error("Couldn't save", message(e)),
  });
  const test = useMutation({
    mutationFn: () => api<GoogleMapsView>(`${URL}/test`, { method: "POST" }),
    onSuccess: (r) => {
      set(r);
      if (r.lastTest?.ok) notify.success("Google answered", r.lastTest.message ?? undefined);
      else notify.error("Test failed", r.lastTest?.message ?? undefined);
    },
    onError: (e) => notify.error("Couldn't test", message(e)),
  });
  return (
    <Section
      title="Google Maps (address autocomplete)"
      description="Suggests full street addresses as people type in the portal and console address fields. Quotes still need only the ZIP; without this, the street is typed by hand. Use a Google Cloud key with only the Places API (New) enabled."
      actions={v ? <StatusBadge domain="integration" value={v.status} /> : null}
    >
      <IntegrationBody q={q} canManage={canManage}>
        {v ? (
          <>
            <DefinitionList
              items={[
                { term: "API key", detail: v.keySaved ? "Saved" : "Missing" },
                { term: "Last test", detail: v.lastTest ? `${when.format(new Date(v.lastTest.at))}: ${v.lastTest.message ?? (v.lastTest.ok ? "Passed" : "Failed")}` : "Never" },
              ]}
            />
            {canManage ? (
              <>
                <form
                  className="ffa-inlineform"
                  autoComplete="off"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (key.trim()) save.mutate(v.enabled);
                  }}
                >
                  <TextField label="Google Maps API key" type="password" autoComplete="new-password" spellCheck={false} value={key} onChange={(e) => setKey(e.target.value)} hint={v.keySaved ? "Saved. Leave blank to keep it." : "Starts with AIza"} />
                  <Button type="submit" size="lg" variant="primary" disabled={!key.trim()} loading={save.isPending}>
                    Save
                  </Button>
                </form>
                <div className="ffa-btnrow">
                  <Toggle look="switch" label="Address suggestions on" checked={v.enabled} disabled={!v.configured || save.isPending} onChange={(on) => save.mutate(on)} />
                  <Button disabled={!v.configured} loading={test.isPending} onClick={() => test.mutate()}>
                    Test
                  </Button>
                </div>
              </>
            ) : null}
          </>
        ) : null}
      </IntegrationBody>
    </Section>
  );
}
